/* eslint-disable no-confusing-arrow */
import { Add, Close, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { DemeterPageElementModel, PresentationTemplateSlideModel } from '../../../Generated/Raven-Demeter';
import IconButton from '../../Components/Form/Buttons/IconButton';
import styles from './PresentationTemplatesPage.module.scss'; // IMPORTANT: ten plik ma style sortable_list*

interface IPresentationTemplateSlidesListProps {
    slides: PresentationTemplateSlideModel[] | undefined;

    height?: number;
    emptyContent?: React.ReactNode;

    allowDelete?: boolean;

    renderSlideHeader: (slide: PresentationTemplateSlideModel, slideIndex: number) => React.ReactNode;
    renderItem: (item: DemeterPageElementModel) => React.ReactNode;

    onAddItem?: () => void;

    onChange?: (slides: PresentationTemplateSlideModel[] | undefined) => void;

    onSelectSlide?: (slideIndex: number, slide: PresentationTemplateSlideModel) => void;
    onSelectItem?: (slideIndex: number, slide: PresentationTemplateSlideModel, item: DemeterPageElementModel) => void;

    onUnselect?: () => void;
}

type Selection = { kind: 'slide'; slideIndex: number } | { kind: 'item'; slideIndex: number; itemIndex: number } | null;

interface UiItem {
    id: number;
    value: DemeterPageElementModel;
}

interface UiSlide {
    id: number;
    value: PresentationTemplateSlideModel;
    rows: UiItem[];
}

const PresentationTemplateSlidesList: React.FC<IPresentationTemplateSlidesListProps> = (props) => {
    const [uiSlides, setUiSlides] = useState<UiSlide[]>([]);
    const [selection, setSelection] = useState<Selection>(null);

    const nextId = useRef(1);
    const prevSlidesRef = useRef<PresentationTemplateSlideModel[] | undefined>(undefined);
    const isLocalUpdate = useRef(false);

    const toOutputModel = (slides: UiSlide[]): PresentationTemplateSlideModel[] =>
        slides.map((s, index) => ({
            ...s.value,
            order: s.value.order ?? index,
            rows: s.rows.map((r) => r.value),
        }));

    useEffect(() => {
        if (isLocalUpdate.current) {
            isLocalUpdate.current = false;
            return;
        }

        if (props.slides === prevSlidesRef.current) {
            return;
        }

        const source = props.slides ?? [];

        setUiSlides((prevUi) => {
            const mapped: UiSlide[] = source.map((slide, slideIndex) => {
                const prevSlide = prevUi[slideIndex];
                const slideId =
                    prevSlide?.id ??
                    (() => {
                        nextId.current += 1;
                        return nextId.current;
                    })();

                const prevRows = prevSlide?.rows ?? [];

                return {
                    id: slideId,
                    value: slide,
                    rows: (slide.rows ?? []).map((item, itemIndex) => {
                        const prevItem = prevRows[itemIndex];
                        const itemId =
                            prevItem?.id ??
                            (() => {
                                nextId.current += 1;
                                return nextId.current;
                            })();

                        return { id: itemId, value: item };
                    }),
                };
            });

            setSelection((prevSel) => {
                if (!prevSel) {
                    return null;
                }

                if (prevSel.kind === 'slide') {
                    if (prevSel.slideIndex < 0 || prevSel.slideIndex >= mapped.length) {
                        props.onUnselect?.();
                        return null;
                    }
                    return prevSel;
                }

                if (prevSel.slideIndex < 0 || prevSel.slideIndex >= mapped.length) {
                    props.onUnselect?.();
                    return null;
                }

                const rows = mapped[prevSel.slideIndex]?.rows ?? [];
                if (prevSel.itemIndex < 0 || prevSel.itemIndex >= rows.length) {
                    props.onUnselect?.();
                    return null;
                }

                return prevSel;
            });

            return mapped;
        });

        prevSlidesRef.current = props.slides;
    }, [props.slides, props.onUnselect]);

    const emitChange = (newSlides: UiSlide[]) => {
        props.onChange?.(toOutputModel(newSlides));
    };

    const move = (direction: number) => {
        if (!selection) {
            return;
        }

        if (selection.kind === 'slide') {
            const index = selection.slideIndex;
            const newIndex = index + direction;

            if (newIndex < 0 || newIndex >= uiSlides.length) {
                return;
            }

            const newSlides = [...uiSlides];
            [newSlides[index], newSlides[newIndex]] = [newSlides[newIndex], newSlides[index]];

            isLocalUpdate.current = true;
            setUiSlides(newSlides);
            setSelection({ kind: 'slide', slideIndex: newIndex });
            emitChange(newSlides);
            return;
        }

        const { slideIndex, itemIndex } = selection;
        const slide = uiSlides[slideIndex];
        if (!slide) {
            return;
        }

        const newItemIndex = itemIndex + direction;
        if (newItemIndex < 0 || newItemIndex >= slide.rows.length) {
            return;
        }

        const newSlides = [...uiSlides];
        const newSlide: UiSlide = { ...slide, rows: [...slide.rows] };

        [newSlide.rows[itemIndex], newSlide.rows[newItemIndex]] = [newSlide.rows[newItemIndex], newSlide.rows[itemIndex]];
        newSlides[slideIndex] = newSlide;

        isLocalUpdate.current = true;
        setUiSlides(newSlides);
        setSelection({ kind: 'item', slideIndex, itemIndex: newItemIndex });
        emitChange(newSlides);
    };

    const handleDelete = () => {
        if (!selection) {
            return;
        }

        if (selection.kind === 'slide') {
            const idx = selection.slideIndex;
            if (idx < 0 || idx >= uiSlides.length) {
                return;
            }

            const newSlides = uiSlides.filter((_, index) => index !== idx);

            isLocalUpdate.current = true;
            setUiSlides(newSlides);
            setSelection(null);
            props.onUnselect?.();
            emitChange(newSlides);
            return;
        }

        const { slideIndex, itemIndex } = selection;
        const slide = uiSlides[slideIndex];
        if (!slide) {
            return;
        }

        const newItems = slide.rows.filter((_, index) => index !== itemIndex);

        let newSlides: UiSlide[];

        if (newItems.length === 0) {
            newSlides = uiSlides.filter((_, index) => index !== slideIndex);
        } else {
            newSlides = [...uiSlides];
            newSlides[slideIndex] = { ...slide, rows: newItems };
        }

        isLocalUpdate.current = true;
        setUiSlides(newSlides);
        setSelection(null);
        props.onUnselect?.();
        emitChange(newSlides);
    };

    const isMoveUpDisabled = (() => {
        if (!selection) {
            return true;
        }

        if (selection.kind === 'slide') {
            return selection.slideIndex <= 0;
        }

        return selection.itemIndex <= 0;
    })();

    const isMoveDownDisabled = (() => {
        if (!selection) {
            return true;
        }

        if (selection.kind === 'slide') {
            return selection.slideIndex >= uiSlides.length - 1;
        }

        const slide = uiSlides[selection.slideIndex];
        if (!slide) {
            return true;
        }

        return selection.itemIndex >= slide.rows.length - 1;
    })();

    const isDeleteDisabled = !selection;
    const shouldShowEmptyState = uiSlides.length === 0 && !!props.emptyContent;

    const toggleSlideSelection = (slideIndex: number, slide: PresentationTemplateSlideModel) => {
        setSelection((prev) => {
            const isSameSlideSelected = prev?.kind === 'slide' && prev.slideIndex === slideIndex;

            if (isSameSlideSelected) {
                props.onUnselect?.();
                return null;
            }

            props.onSelectSlide?.(slideIndex, slide);
            return { kind: 'slide', slideIndex };
        });
    };

    const toggleItemSelection = (slideIndex: number, itemIndex: number, slide: PresentationTemplateSlideModel, item: DemeterPageElementModel) => {
        setSelection((prev) => {
            const isSameItemSelected = prev?.kind === 'item' && prev.slideIndex === slideIndex && prev.itemIndex === itemIndex;

            if (isSameItemSelected) {
                props.onUnselect?.();
                return null;
            }

            props.onSelectItem?.(slideIndex, slide, item);
            return { kind: 'item', slideIndex, itemIndex };
        });
    };

    return (
        <div className={styles.sortable_list}>
            <div className={styles.sortable_list_items} style={props.height ? { height: `${props.height}px` } : {}}>
                {uiSlides.map((slide, slideIndex) => {
                    const slideSelected = selection?.kind === 'slide' && selection.slideIndex === slideIndex;

                    return (
                        <div key={slide.id}>
                            <div
                                className={slideSelected ? styles.sortable_list_item_selected : styles.sortable_list_item}
                                onClick={() => toggleSlideSelection(slideIndex, slide.value)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={() => {}}
                                style={{ fontWeight: 700 }}
                            >
                                {props.renderSlideHeader(slide.value, slideIndex)}
                            </div>

                            {slide.rows.map((item, itemIndex) => {
                                const itemSelected = selection?.kind === 'item' && selection.slideIndex === slideIndex && selection.itemIndex === itemIndex;

                                return (
                                    <div
                                        key={item.id}
                                        className={itemSelected ? styles.sortable_list_item_selected : styles.sortable_list_item}
                                        onClick={() => toggleItemSelection(slideIndex, itemIndex, slide.value, item.value)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={() => {}}
                                    >
                                        {props.renderItem(item.value)}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}

                {shouldShowEmptyState && <div className={styles.sortable_list_items_empty}>{props.emptyContent}</div>}
            </div>

            <div className={styles.sortable_list_actions}>
                {props.onAddItem && (
                    <IconButton
                        handleClick={props.onAddItem}
                        color="default"
                        className={styles.sortable_list_action_button}
                        track={{ eventCategory: 'PresentationTemplateEdit', eventAction: 'AddItemClicked' }}
                    >
                        <Add className={styles.sortable_list_action_icon} />
                    </IconButton>
                )}

                <IconButton
                    handleClick={() => move(-1)}
                    color="default"
                    className={styles.sortable_list_action_button}
                    disabled={isMoveUpDisabled}
                    track={{ eventCategory: 'PresentationTemplateEdit', eventAction: 'MoveItemUpClicked' }}
                >
                    <ExpandLess className={styles.sortable_list_action_icon} />
                </IconButton>

                <IconButton
                    handleClick={() => move(1)}
                    color="default"
                    className={styles.sortable_list_action_button}
                    disabled={isMoveDownDisabled}
                    track={{ eventCategory: 'PresentationTemplateEdit', eventAction: 'MoveItemDownClicked' }}
                >
                    <ExpandMore className={styles.sortable_list_action_icon} />
                </IconButton>

                {props.allowDelete && (
                    <IconButton
                        handleClick={handleDelete}
                        color="default"
                        className={styles.sortable_list_action_button}
                        disabled={isDeleteDisabled}
                        track={{ eventCategory: 'PresentationTemplateEdit', eventAction: 'DeleteItemClicked' }}
                    >
                        <Close className={styles.sortable_list_action_icon} />
                    </IconButton>
                )}
            </div>
        </div>
    );
};

export default PresentationTemplateSlidesList;
